import { Car, Bike, Edit2, Save, X, Plus, Minus, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

import React, { useState, useMemo } from "react";
import { useVehicleCard } from "@/hooks/useVehicleCard";
import { MakeSelectionDialog } from "./dialogs/MakeSelectionDialog";
import { ModelSelectionDialog } from "./dialogs/ModelSelectionDialog";
import { EngineSelectionDialog } from "./dialogs/EngineSelectionDialog";
import { VehicleCardVehicleDetails } from "@/types/vehicleCard";
import { formatMileage } from "@/utils/listingUtils";
import { useLanguage } from "@/context/LanguageContext";
import {
  conditionGradeLabels,
  fuelTypeLabels,
  transmissionTypeLabels,
  bodyTypeLabels,
  colorLabels,
  labelToCondition,
  labelToFuelType,
  labelToFuelTypeAnyLanguage,
  labelToTransmissionAnyLanguage,
  labelToConditionAnyLanguage,
  labelToTransmission,
  labelToBodyType,
  labelToColor,
} from "@/i18n/labels/vehicleLabels";
import {
  vehicleCardUILabels,
  vehicleCardToastLabels,
  commonMiscLabels,
  getEquipmentLabel,
} from "@/i18n";
import {
  ConditionGrade,
  VehicleFuelType,
  TransmissionType,
} from "@/types/enums";

/**
 * Resolve a stored value to the exact option label its <Select> renders.
 *
 * A row can hold either the technical key ("white") or an already-localized
 * label in whatever casing the propagation path wrote ("vit"), while the
 * SelectItem values are the canonical labels ("Vit"). Looking the value up by
 * key alone and falling back to it verbatim leaves the trigger blank whenever
 * the casing or the form differs, which is why a card storing "vit" showed an
 * empty Färg field. Match the key first, then the labels case-insensitively.
 */
const toCanonicalOptionLabel = (
  stored: string | undefined | null,
  labelMap: Record<string, string>,
): string => {
  if (!stored) return "";
  const needle = stored.trim().toLowerCase();
  const byKey = labelMap[needle];
  if (byKey) return byKey;
  const byLabel = Object.values(labelMap).find(
    (label) => label.toLowerCase() === needle,
  );
  return byLabel ?? stored;
};

/**
 * Map a select's current label back to a technical key for saving, but leave the
 * stored value untouched when the admin never changed the field - so display-only
 * canonicalization can never rewrite what the row holds.
 */
const keepStoredUnlessRepicked = (
  currentLabel: string,
  storedValue: string | undefined | null,
  labelMap: Record<string, string>,
  reverseMap: Record<string, string>,
): string | undefined => {
  if (!currentLabel) return undefined;
  if (
    storedValue &&
    currentLabel === toCanonicalOptionLabel(storedValue, labelMap)
  ) {
    return storedValue;
  }
  return reverseMap[currentLabel] || currentLabel;
};

interface VehicleCardCarDetailsProps {
  id: string;
  vehicleDetails: VehicleCardVehicleDetails;
}

export const VehicleCardCarDetails = ({
  id,
  vehicleDetails,
}: VehicleCardCarDetailsProps) => {
  const { language } = useLanguage();

  const [showAllEquipment, setShowAllEquipment] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newEquipmentItem, setNewEquipmentItem] = useState("");
  const initialEquipmentCount = 6;

  // Dialog states
  const [makeDialogOpen, setMakeDialogOpen] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [engineDialogOpen, setEngineDialogOpen] = useState(false);

  const { updateVehicleDetailsAsync, isUpdatingVehicleDetails } =
    useVehicleCard();

  // Initialize all state with default values (will be updated after safety check)
  const [currentInitialMake, setCurrentInitialMake] = useState("");
  const [currentInitialModel, setCurrentInitialModel] = useState("");
  const [currentInitialVariant, setCurrentInitialVariant] = useState("");
  const [currentInitialTrim, setCurrentInitialTrim] = useState("");
  const [currentInitialYear, setCurrentInitialYear] = useState(0);
  const [currentInitialCondition, setCurrentInitialCondition] = useState("");
  const [currentInitialMileage, setCurrentInitialMileage] = useState(0);
  const [currentInitialEngine, setCurrentInitialEngine] = useState("");
  const [currentInitialTransmission, setCurrentInitialTransmission] =
    useState("");
  const [currentInitialBodyType, setCurrentInitialBodyType] = useState("");
  const [currentInitialColor, setCurrentInitialColor] = useState("");
  const [currentInitialFuelType, setCurrentInitialFuelType] = useState("");
  const [currentInitialHorsepower, setCurrentInitialHorsepower] = useState("");
  const [currentInitialEngineSize, setCurrentInitialEngineSize] = useState("");
  const [
    currentInitialElectricDriveRange,
    setCurrentInitialElectricDriveRange,
  ] = useState<number | undefined>(undefined);
  const [currentInitialVin, setCurrentInitialVin] = useState("");
  const [currentInitialEquipment, setCurrentInitialEquipment] = useState<
    string[]
  >([]);

  // Editable state
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [variant, setVariant] = useState("");
  const [trim, setTrim] = useState("");
  const [year, setYear] = useState(0);
  const [condition, setCondition] = useState("");
  const [mileage, setMileage] = useState(0);
  const [engine, setEngine] = useState("");
  const [transmission, setTransmission] = useState("");
  const [bodyType, setBodyType] = useState("");
  const [color, setColor] = useState("");
  const [fuelType, setFuelType] = useState("");
  const [horsepower, setHorsepower] = useState("");
  const [engineSize, setEngineSize] = useState("");
  const [electricDriveRange, setElectricDriveRange] = useState<
    number | undefined
  >(undefined);
  const [vin, setVin] = useState("");
  const [equipment, setEquipment] = useState<string[]>([]);

  // Check if any values have changed from baseline
  const hasChanges = useMemo(() => {
    return (
      make !== currentInitialMake ||
      model !== currentInitialModel ||
      variant !== currentInitialVariant ||
      trim !== currentInitialTrim ||
      year !== currentInitialYear ||
      condition !== currentInitialCondition ||
      mileage !== currentInitialMileage ||
      engine !== currentInitialEngine ||
      transmission !== currentInitialTransmission ||
      bodyType !== currentInitialBodyType ||
      color !== currentInitialColor ||
      fuelType !== currentInitialFuelType ||
      horsepower !== currentInitialHorsepower ||
      engineSize !== currentInitialEngineSize ||
      electricDriveRange !== currentInitialElectricDriveRange ||
      vin !== currentInitialVin ||
      equipment.length !== currentInitialEquipment.length ||
      equipment.some((item, index) => item !== currentInitialEquipment[index])
    );
  }, [
    make,
    currentInitialMake,
    model,
    currentInitialModel,
    variant,
    currentInitialVariant,
    trim,
    currentInitialTrim,
    year,
    currentInitialYear,
    condition,
    currentInitialCondition,
    mileage,
    currentInitialMileage,
    engine,
    currentInitialEngine,
    transmission,
    currentInitialTransmission,
    bodyType,
    currentInitialBodyType,
    color,
    currentInitialColor,
    fuelType,
    currentInitialFuelType,
    horsepower,
    currentInitialHorsepower,
    engineSize,
    currentInitialEngineSize,
    electricDriveRange,
    currentInitialElectricDriveRange,
    vin,
    currentInitialVin,
    equipment,
    currentInitialEquipment,
  ]);

  // Update state with actual values from vehicleDetails
  React.useEffect(() => {
    if (vehicleDetails) {
      const {
        make: initialMake,
        model: initialModel,
        variant: initialVariant = "",
        trim: initialTrim = "",
        year: initialYear,
        condition: initialCondition = "",
        mileage: initialMileage,
        engine: initialEngine,
        transmission: initialTransmission,
        bodyType: initialBodyType,
        color: initialColor,
        fuelType: initialFuelType = "",
        horsepower: initialHorsepower,
        engineSize: initialEngineSize = "",
        electricDriveRange: initialElectricDriveRange,
        vin: initialVin = "",
        equipment: initialEquipment = [],
      } = vehicleDetails;

      setCurrentInitialMake(initialMake);
      setCurrentInitialModel(initialModel);
      setCurrentInitialVariant(initialVariant);
      setCurrentInitialTrim(initialTrim);
      setCurrentInitialYear(initialYear);
      setCurrentInitialCondition(initialCondition);
      setCurrentInitialMileage(initialMileage);
      setCurrentInitialEngine(initialEngine);
      setCurrentInitialTransmission(initialTransmission);
      setCurrentInitialBodyType(initialBodyType);
      setCurrentInitialColor(initialColor);
      setCurrentInitialFuelType(initialFuelType);
      setCurrentInitialHorsepower(
        initialHorsepower ? String(initialHorsepower) : "",
      );
      setCurrentInitialEngineSize(initialEngineSize);
      setCurrentInitialElectricDriveRange(initialElectricDriveRange);
      setCurrentInitialVin(initialVin);
      setCurrentInitialEquipment(initialEquipment);

      setMake(initialMake);
      setModel(initialModel);
      setVariant(initialVariant);
      setTrim(initialTrim);
      setYear(initialYear);
      // Convert technical values to labels for display in dropdowns (uses current language)
      // Normalize to uppercase for enum-based lookups
      const lang = language;
      const conditionLabels = conditionGradeLabels(lang);
      const transmissionLabels = transmissionTypeLabels(lang);
      const bodyTypeLabelsMap = bodyTypeLabels(lang);
      const colorLabelsMap = colorLabels(lang);
      const fuelTypeLabelsMap = fuelTypeLabels(lang);

      setCondition(
        initialCondition
          ? (conditionLabels[
              initialCondition.toUpperCase() as ConditionGrade
            ] ?? "")
          : "",
      );
      setMileage(initialMileage);
      setEngine(initialEngine);
      setTransmission(
        initialTransmission
          ? (transmissionLabels[
              initialTransmission.toUpperCase() as TransmissionType
            ] ?? "")
          : "",
      );
      // Convert technical values to labels for display in dropdowns
      setBodyType(toCanonicalOptionLabel(initialBodyType, bodyTypeLabelsMap));
      setColor(toCanonicalOptionLabel(initialColor, colorLabelsMap));
      setFuelType(
        initialFuelType
          ? (fuelTypeLabelsMap[
              initialFuelType.toUpperCase() as VehicleFuelType
            ] ?? "")
          : "",
      );
      setHorsepower(initialHorsepower ? String(initialHorsepower) : "");
      setEngineSize(initialEngineSize);
      setElectricDriveRange(initialElectricDriveRange);
      setVin(initialVin);
      setEquipment(initialEquipment);
    }
  }, [vehicleDetails]);

  // Safety check for vehicleDetails
  if (!vehicleDetails) {
    return (
      <div className="text-center text-muted-foreground p-4">
        {vehicleCardUILabels(language).vehicleDetailsNotLoaded}
      </div>
    );
  }

  const updateInitialValues = () => {
    setCurrentInitialMake(make);
    setCurrentInitialModel(model);
    setCurrentInitialVariant(variant);
    setCurrentInitialTrim(trim);
    setCurrentInitialYear(year);
    setCurrentInitialCondition(condition);
    setCurrentInitialMileage(mileage);
    setCurrentInitialEngine(engine);
    setCurrentInitialTransmission(transmission);
    setCurrentInitialBodyType(bodyType);
    setCurrentInitialColor(color);
    setCurrentInitialFuelType(fuelType);
    setCurrentInitialHorsepower(horsepower);
    setCurrentInitialEngineSize(engineSize);
    setCurrentInitialElectricDriveRange(electricDriveRange);
    setCurrentInitialVin(vin);
    setCurrentInitialEquipment(equipment);
  };

  const handleSave = async () => {
    try {
      // Convert label values back to technical enum values before sending to backend (uses current language)
      const lang = language;
      // The three ENUM selects reverse against BOTH locales: the select state was seeded
      // in whatever language was active when the dialog opened, and that effect does not
      // re-run on a language switch. Reversing only against the current language sent the
      // raw Swedish label ("Bensin", "Manuell", "Utmärkt") to an enum column -> 500.
      const conditionReverseMap = labelToConditionAnyLanguage();
      const transmissionReverseMap = labelToTransmissionAnyLanguage();
      const fuelTypeReverseMap = labelToFuelTypeAnyLanguage();
      // bodyType/color are free-text columns, so a stale label is cosmetic, not a 500;
      // they keep the language-scoped maps their keepStoredUnlessRepicked logic needs.
      const bodyTypeReverseMap = labelToBodyType(lang);
      const colorReverseMap = labelToColor(lang);

      const updatedVehicleDetails = {
        ...vehicleDetails,
        make,
        model,
        variant,
        trim,
        year,
        condition: condition
          ? conditionReverseMap[condition] || condition
          : undefined,
        mileage,
        engine,
        transmission: transmission
          ? transmissionReverseMap[transmission] || transmission
          : undefined,
        // Canonicalizing for display must not rewrite storage on its own: a row
        // holding "vit" now shows "Vit" in the select, and blindly reversing that
        // would persist "white" on a save where the admin never touched Färg.
        // Only map back when the pick actually differs from what is stored.
        bodyType: keepStoredUnlessRepicked(
          bodyType,
          vehicleDetails.bodyType,
          bodyTypeLabels(lang),
          bodyTypeReverseMap,
        ),
        color: keepStoredUnlessRepicked(
          color,
          vehicleDetails.color,
          colorLabels(lang),
          colorReverseMap,
        ),
        fuelType: fuelType
          ? fuelTypeReverseMap[fuelType] || fuelType
          : undefined,
        horsepower: horsepower || undefined,
        engineSize: engineSize || undefined,
        electricDriveRange: electricDriveRange || undefined,
        vin,
        equipment,
      };

      const result = await updateVehicleDetailsAsync({
        vehicleCardId: id,
        vehicleDetails: updatedVehicleDetails,
      });

      // Check if mutation was actually successful
      if (!result || !result.success) {
        throw new Error("Mutation did not return success");
      }

      const toastLabels = vehicleCardToastLabels(language);
      toast.success(toastLabels.vehicleDetailsUpdated, { description: toastLabels.vehicleDetailsUpdatedDescription });

      // Update baseline values after successful save
      updateInitialValues();

      setIsEditing(false);
    } catch (error: unknown) {
      // Determine error message
      const uiLabels = vehicleCardUILabels(language);
      const toastLabels = vehicleCardToastLabels(language);
      let errorMessage = toastLabels.saveErrorDescription;
      if (error && typeof error === "object" && "response" in error) {
        const err = error as { response?: { status?: number } };
        if (err.response?.status === 401) {
          errorMessage = uiLabels.unauthorized;
        }
      }
      if (error && typeof error === "object" && "message" in error) {
        const err = error as { message?: string };
        if (err.message) {
          errorMessage = err.message;
        }
      }
      if (error && typeof error === "object" && "details" in error) {
        const err = error as { details?: { message?: string } };
        if (err.details?.message) {
          errorMessage = err.details.message;
        }
      }

      toast.error(toastLabels.saveError, { description: errorMessage });
    }
  };

  const handleCancel = () => {
    // Reset to current baseline values (latest saved state)
    // Convert technical enum values to labels for dropdowns (uses current language)
    const lang = language;
    const conditionLabels = conditionGradeLabels(lang);
    const transmissionLabels = transmissionTypeLabels(lang);
    const bodyTypeLabelsMap = bodyTypeLabels(lang);
    const colorLabelsMap = colorLabels(lang);
    const fuelTypeLabelsMap = fuelTypeLabels(lang);

    setMake(currentInitialMake);
    setModel(currentInitialModel);
    setVariant(currentInitialVariant);
    setTrim(currentInitialTrim);
    setYear(currentInitialYear);
    setCondition(
      currentInitialCondition
        ? (conditionLabels[
            currentInitialCondition.toUpperCase() as ConditionGrade
          ] ?? "")
        : "",
    );
    setMileage(currentInitialMileage);
    setEngine(currentInitialEngine);
    setTransmission(
      currentInitialTransmission
        ? (transmissionLabels[
            currentInitialTransmission.toUpperCase() as TransmissionType
          ] ?? "")
        : "",
    );
    setBodyType(
      toCanonicalOptionLabel(currentInitialBodyType, bodyTypeLabelsMap),
    );
    setColor(toCanonicalOptionLabel(currentInitialColor, colorLabelsMap));
    setFuelType(
      currentInitialFuelType
        ? (fuelTypeLabelsMap[
            currentInitialFuelType.toUpperCase() as VehicleFuelType
          ] ?? "")
        : "",
    );
    setHorsepower(currentInitialHorsepower);
    setEngineSize(currentInitialEngineSize);
    setElectricDriveRange(currentInitialElectricDriveRange);
    setVin(currentInitialVin);
    setEquipment(currentInitialEquipment);
    setIsEditing(false);
  };

  const addEquipment = () => {
    if (newEquipmentItem.trim()) {
      // If not in edit mode and adding first equipment, activate edit mode
      if (!isEditing && equipment.length === 0) {
        setIsEditing(true);
      }
      setEquipment([...equipment, newEquipmentItem.trim()]);
      setNewEquipmentItem("");
    }
  };

  const removeEquipment = (index: number) => {
    setEquipment(equipment.filter((_, i) => i !== index));
  };

  const displayedEquipment = showAllEquipment
    ? equipment
    : equipment.slice(0, initialEquipmentCount);

  return (
    <div className="space-y-6 relative">
      {/* Loading Overlay */}
      {isUpdatingVehicleDetails && (
        <div className="absolute inset-0 bg-secondary/80 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {vehicleCardUILabels(language).savingVehicleDetails}
            </p>
          </div>
        </div>
      )}

      {/* Vehicle Details Card */}
      <Card>
        <CardHeader className="p-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            {vehicleDetails.vehicleType === "mc" ? (
              <Bike className="h-5 w-5" />
            ) : (
              <Car className="h-5 w-5" />
            )}
            {vehicleCardUILabels(language).vehicleDetails}
            {isEditing && (
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isUpdatingVehicleDetails}
                >
                  <X className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasChanges || isUpdatingVehicleDetails}
                >
                  {isUpdatingVehicleDetails ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-8 w-8 text-blue-700 dark:text-blue-300" />
                  )}
                </Button>
              </div>
            )}
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => setIsEditing(true)}
                disabled={isUpdatingVehicleDetails}
              >
                <Edit2 className="h-8 w-8" />
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-4">
          {/* Basic Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                Reg
              </label>
              <div
                className={`text-sm font-medium ${
                  !vehicleDetails.regNumber
                    ? "text-red-600 dark:text-red-400"
                    : ""
                }`}
              >
                {vehicleDetails.regNumber ||
                  vehicleCardUILabels(language).notSpecified}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).make}
              </label>
              {isEditing ? (
                <Button
                  variant="outline"
                  onClick={() => setMakeDialogOpen(true)}
                  className="h-8 w-full justify-start text-left font-normal"
                  disabled={isUpdatingVehicleDetails}
                >
                  {make || vehicleCardUILabels(language).selectMake}
                </Button>
              ) : (
                <div className="text-sm font-medium">{make}</div>
              )}
            </div>

            <div className="space-y-2">
              <label
                className={`text-sm font-medium ${
                  isEditing && make && !model
                    ? "text-red-500"
                    : "text-muted-foreground"
                }`}
              >
                {vehicleCardUILabels(language).model}
              </label>
              {isEditing ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!make) {
                      const uiLabels = vehicleCardUILabels(language);
                      toast.error(uiLabels.selectMakeFirst, { description: uiLabels.selectMakeFirstDescription });
                      return;
                    }
                    setModelDialogOpen(true);
                  }}
                  className="h-8 w-full justify-start text-left font-normal"
                  disabled={isUpdatingVehicleDetails || !make}
                >
                  {model || vehicleCardUILabels(language).selectModel}
                </Button>
              ) : (
                <div className="text-sm font-medium">{model}</div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).variant}
              </label>
              {isEditing ? (
                <Input
                  value={variant}
                  onChange={(e) => setVariant(e.target.value)}
                  className="h-8"
                  disabled={isUpdatingVehicleDetails}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !variant ? "text-muted-foreground" : ""
                  }`}
                >
                  {variant || commonMiscLabels(language).unknown}
                </div>
              )}
            </div>

            {/* Trim is a separate field from Variant - each saves 1:1 to its own column */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).trim}
              </label>
              {isEditing ? (
                <Input
                  value={trim}
                  onChange={(e) => setTrim(e.target.value)}
                  className="h-8"
                  disabled={isUpdatingVehicleDetails}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !trim ? "text-muted-foreground" : ""
                  }`}
                >
                  {trim || commonMiscLabels(language).unknown}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).year}
              </label>
              {isEditing ? (
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setYear(isNaN(value) ? 0 : value);
                  }}
                  className="h-8"
                  disabled={isUpdatingVehicleDetails}
                />
              ) : (
                <div className="text-sm font-medium">{year}</div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).condition}
              </label>
              {isEditing ? (
                <Select
                  value={condition}
                  onValueChange={(value) => setCondition(value)}
                  disabled={isUpdatingVehicleDetails}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue
                      placeholder={
                        vehicleCardUILabels(language).selectCondition
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(conditionGradeLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !condition ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicleDetails.condition
                    ? (conditionGradeLabels(language)[
                        vehicleDetails.condition as ConditionGrade
                      ] ?? vehicleDetails.condition)
                    : vehicleCardUILabels(language).notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).mileage}
              </label>
              {isEditing ? (
                <Input
                  type="number"
                  value={mileage}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setMileage(isNaN(value) ? 0 : value);
                  }}
                  className="h-8"
                  disabled={isUpdatingVehicleDetails}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !mileage ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {mileage ? `${formatMileage(mileage)} mil` : "Ej angivet"}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label
                className={`text-sm font-medium ${
                  isEditing && make && !engine
                    ? "text-red-500"
                    : "text-muted-foreground"
                }`}
              >
                {vehicleCardUILabels(language).engine}
              </label>
              {isEditing ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    if (!make) {
                      const uiLabels = vehicleCardUILabels(language);
                      toast.error(uiLabels.selectMakeFirst, { description: uiLabels.selectMakeFirstForEngine });
                      return;
                    }
                    setEngineDialogOpen(true);
                  }}
                  className="h-8 w-full justify-start text-left font-normal"
                  disabled={isUpdatingVehicleDetails || !make}
                >
                  {engine || vehicleCardUILabels(language).selectEngine}
                </Button>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !engine ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {engine || vehicleCardUILabels(language).notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).transmission}
              </label>
              {isEditing ? (
                <Select
                  value={transmission}
                  onValueChange={(value) => setTransmission(value)}
                  disabled={isUpdatingVehicleDetails}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue
                      placeholder={
                        vehicleCardUILabels(language).selectTransmission
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(transmissionTypeLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !transmission ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicleDetails.transmission
                    ? (transmissionTypeLabels(language)[
                        vehicleDetails.transmission as TransmissionType
                      ] ?? vehicleDetails.transmission)
                    : vehicleCardUILabels(language).notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).bodyType}
              </label>
              {isEditing ? (
                <Select
                  value={bodyType}
                  onValueChange={(value) => setBodyType(value)}
                  disabled={isUpdatingVehicleDetails}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Välj karosstyp" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(bodyTypeLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !bodyType ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicleDetails.bodyType
                    ? toCanonicalOptionLabel(
                        vehicleDetails.bodyType,
                        bodyTypeLabels(language),
                      )
                    : vehicleCardUILabels(language).notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).color}
              </label>
              {isEditing ? (
                <Select
                  value={color}
                  onValueChange={(value) => setColor(value)}
                  disabled={isUpdatingVehicleDetails}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue
                      placeholder={vehicleCardUILabels(language).selectColor}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(colorLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !color ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicleDetails.color
                    ? toCanonicalOptionLabel(
                        vehicleDetails.color,
                        colorLabels(language),
                      )
                    : vehicleCardUILabels(language).notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).fuelType}
              </label>
              {isEditing ? (
                <Select
                  value={fuelType}
                  onValueChange={(value) => setFuelType(value)}
                  disabled={isUpdatingVehicleDetails}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue
                      placeholder={vehicleCardUILabels(language).selectFuelType}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(fuelTypeLabels(language)).map(
                      ([techKey, label]) => (
                        <SelectItem key={techKey} value={label}>
                          {label}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !fuelType ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vehicleDetails.fuelType
                    ? (fuelTypeLabels(language)[
                        vehicleDetails.fuelType as VehicleFuelType
                      ] ?? vehicleDetails.fuelType)
                    : vehicleCardUILabels(language).notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).horsepower}
              </label>
              {isEditing ? (
                <Input
                  type="number"
                  value={horsepower}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setHorsepower(isNaN(value) ? "" : String(value));
                  }}
                  className="h-8"
                  disabled={isUpdatingVehicleDetails}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !horsepower ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {horsepower || vehicleCardUILabels(language).notSpecified}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).engineSize}
              </label>
              {isEditing ? (
                <Input
                  value={engineSize}
                  onChange={(e) => setEngineSize(e.target.value)}
                  className="h-8"
                  placeholder={
                    vehicleCardUILabels(language).engineSizePlaceholder
                  }
                  disabled={isUpdatingVehicleDetails}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${!engineSize ? "text-muted-foreground" : ""}`}
                >
                  {engineSize || commonMiscLabels(language).unknown}
                </div>
              )}
            </div>

            {/* Electric Drive Range - only show for electric vehicles */}
            {(vehicleDetails.fuelType === "El" ||
              vehicleDetails.fuelType === "EL" ||
              vehicleDetails.fuelType === "ELECTRIC" ||
              vehicleDetails.fuelType === "Elbil") && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {vehicleCardUILabels(language).electricRangeWltp}
                </label>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={electricDriveRange || ""}
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        setElectricDriveRange(isNaN(value) ? undefined : value);
                      }}
                      className="h-8"
                      disabled={isUpdatingVehicleDetails}
                      placeholder={vehicleCardUILabels(language).enterRange}
                    />
                    <span className="text-sm text-muted-foreground">km</span>
                  </div>
                ) : (
                  <div className="text-sm font-medium">
                    {electricDriveRange
                      ? `${electricDriveRange} km`
                      : vehicleCardUILabels(language).notSpecified}
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).vin}
              </label>
              {isEditing ? (
                <Input
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  className="h-8"
                  disabled={isUpdatingVehicleDetails}
                />
              ) : (
                <div
                  className={`text-sm font-medium ${
                    !vin ? "text-red-600 dark:text-red-400" : ""
                  }`}
                >
                  {vin || vehicleCardUILabels(language).notSpecified}
                </div>
              )}
            </div>
          </div>

          {/* Divider before Equipment Section */}
          <div className="border-t border-border pt-4 mt-4"></div>

          {/* Equipment Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-muted-foreground">
                {vehicleCardUILabels(language).equipment}
              </label>
            </div>

            {/* Always show input field when editing or when no equipment exists */}
            {(isEditing || equipment.length === 0) && (
              <div className="flex gap-2">
                <Input
                  value={newEquipmentItem}
                  onChange={(e) => setNewEquipmentItem(e.target.value)}
                  placeholder={
                    vehicleCardUILabels(language).addEquipmentPlaceholder
                  }
                  className="h-8 w-48"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addEquipment();
                    }
                  }}
                  disabled={isUpdatingVehicleDetails}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addEquipment}
                  disabled={
                    !newEquipmentItem.trim() || isUpdatingVehicleDetails
                  }
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}

            {equipment.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {displayedEquipment.map((item, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {getEquipmentLabel(item, language)}
                    {isEditing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                        onClick={() => removeEquipment(index)}
                        disabled={isUpdatingVehicleDetails}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                    )}
                  </Badge>
                ))}
                {equipment.length > initialEquipmentCount && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAllEquipment(!showAllEquipment)}
                    className="text-muted-foreground"
                    disabled={isUpdatingVehicleDetails}
                  >
                    {showAllEquipment
                      ? vehicleCardUILabels(language).showLess
                      : vehicleCardUILabels(language).moreEquipment?.replace(
                          "{count}",
                          String(equipment.length - initialEquipmentCount),
                        )}
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                {vehicleCardUILabels(language).noEquipmentSpecified}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <MakeSelectionDialog
        open={makeDialogOpen}
        onOpenChange={setMakeDialogOpen}
        selectedMake={make}
        onSelect={(newMake) => {
          const makeChanged = newMake !== make;
          setMake(newMake);
          // Reset model and engine when make changes
          if (makeChanged) {
            setModel("");
            setEngine("");
          }
        }}
        vehicleType={vehicleDetails.vehicleType === "mc" ? "mc" : "car"}
      />

      <ModelSelectionDialog
        open={modelDialogOpen}
        onOpenChange={setModelDialogOpen}
        selectedMake={make}
        selectedModel={model}
        onSelect={(newModel) => {
          const modelChanged = newModel !== model;
          setModel(newModel);
          // Reset engine when model changes
          if (modelChanged) {
            setEngine("");
          }
        }}
      />

      <EngineSelectionDialog
        open={engineDialogOpen}
        onOpenChange={setEngineDialogOpen}
        selectedMake={make}
        selectedEngine={engine}
        onSelect={setEngine}
      />
    </div>
  );
};
